/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable array-callback-return */
/*

Highcharts Editor

Copyright (c) 2016-2017, Highsoft

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

*/

import { isStr, isArr, generateRandomColors, setAttr, merge } from './highcharts-editor';

function deleteAxisCategories(customizedOptions, type) {
  if (customizedOptions[type]) {
    if (isArr(customizedOptions[type])) delete customizedOptions[type][0].categories;
    else delete customizedOptions[type].categories;
  }
}

// Quick fix. Original heatmap function relies on the chart being made in the editor
// This is the function that doesnt rely on that. Used in the share chart page
const parseShowHeatMapData = (data) => {
  let xAxisCategories = [];
  let yAxisCategories = [],
    xCount = -1,
    yCount = -1;

  let shouldParseXVals = true;
  let shouldParseYVals = true;

  const shouldParse = data.slice(1).every((d) => {
    if (!isStr(d[0])) shouldParseXVals = false;
    if (!isStr(d[1])) shouldParseYVals = false;

    return shouldParseXVals || shouldParseYVals;
  });

  if (!shouldParse) {
    data.forEach((row) => {
      row.splice(0, 0, row[0], row[1]);
    });

    return data;
  }
  if (data[0]) data[0].splice(0, 0, data[0][0], data[0][1]);

  data.slice(1).forEach((element) => {
    let xValue = element[0];
    let yValue = element[1];
    let x = 0,
      y = 0,
      xIndex = null,
      yIndex = null;

    element.splice(0, 0, element[0], element[1]);
    if (shouldParseXVals) {
      if (xValue === undefined) xValue = '';
      xIndex = xAxisCategories.indexOf(xValue.replace(/"/g, ''));
      if (xIndex === -1) {
        xAxisCategories.push(xValue.replace(/"/g, ''));
        xCount++;
        x = xCount;
      } else x = xIndex;

      element[2] = x;
    }

    if (shouldParseYVals) {
      if (yValue === undefined) yValue = '';
      yIndex = yAxisCategories.indexOf(yValue.replace(/"/g, ''));

      if (yIndex === -1) {
        yAxisCategories.push(yValue.replace(/"/g, ''));
        yCount++;
        y = yCount;
      } else y = yIndex;

      element[3] = y;
    }
  });

  return data;
};

export function parseHeatMapData(data, chartPreview, chartOptions) {
  if (!chartOptions && !chartPreview) {
    return parseShowHeatMapData(data);
  }

  let xAxisCategories = [];
  let yAxisCategories = [],
    xCount = -1,
    yCount = -1;

  let shouldParseXVals = true;
  let shouldParseYVals = true;

  const shouldParse = data.slice(1).every((d) => {
    if (!isStr(d[0])) shouldParseXVals = false;
    if (!isStr(d[1])) shouldParseYVals = false;

    return shouldParseXVals || shouldParseYVals;
  });

  if (!shouldParse) {
    let customizedOptions = chartOptions || chartPreview.options.getCustomized();
    deleteAxisCategories(customizedOptions, 'xAxis');
    deleteAxisCategories(customizedOptions, 'yAxis');

    data.forEach((row) => {
      row.splice(0, 0, row[0], row[1]);
    });

    return data;
  }

  if (data[0]) data[0].splice(0, 0, data[0][0], data[0][1]);

  data.slice(1).forEach((element) => {
    let xValue = element[0];
    let yValue = element[1];
    let x = 0,
      y = 0,
      xIndex = null,
      yIndex = null;

    element.splice(0, 0, element[0], element[1]);

    if (shouldParseXVals) {
      if (xValue === undefined) xValue = '';
      xIndex = xAxisCategories.indexOf(xValue.replace(/"/g, ''));
      if (xIndex === -1) {
        xAxisCategories.push(xValue.replace(/"/g, ''));
        xCount++;
        x = xCount;
      } else x = xIndex;

      element[2] = x;
    }

    if (shouldParseYVals) {
      if (yValue === undefined) yValue = '';
      yIndex = yAxisCategories.indexOf(yValue.replace(/"/g, ''));

      if (yIndex === -1) {
        yAxisCategories.push(yValue.replace(/"/g, ''));
        yCount++;
        y = yCount;
      } else y = yIndex;

      element[3] = y;
    }
  });

  if (shouldParseXVals) {
    if (chartOptions) {
      // Used in wizard, find a way to combine these when merging editors
      setAttr(chartOptions, 'xAxis--categories', xAxisCategories, 0);
    } else {
      // Used in data table
      chartPreview.options.set('xAxis--categories', xAxisCategories, 0, true);
    }
  } else {
    let customizedOptions = chartOptions || chartPreview.options.getCustomized();
    deleteAxisCategories(customizedOptions, 'xAxis');
  }
  if (shouldParseYVals) {
    if (chartOptions) {
      setAttr(chartOptions, 'yAxis--categories', yAxisCategories, 0);
    } else {
      chartPreview.options.set('yAxis--categories', yAxisCategories, 0, true);
    }
  } else {
    let customizedOptions = chartOptions || chartPreview.options.getCustomized();
    deleteAxisCategories(customizedOptions, 'yAxis');
  }

  // console.log(data);

  return data;
}

export function parsePackedBubbleAssigns(data, assignOptions, chartPreview, chartOptions) {
  const filteredData = data[0].filter((d) => d !== 'ev-label');
  const seriesAmount = filteredData.length / 2;
  const options = [];
  let customizedOptions = chartOptions ?? chartPreview?.options?.getCustomized();
  const hasLabels = assignOptions[0].label.value;

  for (let i = 0; i < seriesAmount; i++) {
    let opt = {
      [assignOptions[0].name.linkedTo]: i * 2,
      [assignOptions[0].value.linkedTo]: i * 2 + 1
    };

    if (hasLabels) {
      opt[assignOptions[0].label.linkedTo] = filteredData.length + i;
    }

    options.push(opt);
  }

  if (customizedOptions) {
    if (hasLabels) {
      const dataLabelOptions = {
        dataLabels: {
          enabled: true,
          format: '{point.label}'
        }
      };

      if (customizedOptions?.plotOptions) {
        const seriesPlotOptions = customizedOptions.plotOptions.series;
        merge(seriesPlotOptions, dataLabelOptions);
        if (chartPreview && customizedOptions) chartPreview.options.setAll(customizedOptions);
      } else if (chartOptions) {
        merge(
          {
            plotOptions: {
              series: dataLabelOptions
            }
          },
          customizedOptions
        );
      } else if (chartPreview) {
        chartPreview.options.setAll(
          merge(
            {
              plotOptions: {
                series: dataLabelOptions
              }
            },
            customizedOptions
          )
        );
      }
    }
    if (customizedOptions.series?.length !== seriesAmount) {
      // Adjust series based on new length
      if (customizedOptions.series?.length > seriesAmount) {
        customizedOptions.series.splice(seriesAmount, customizedOptions?.series?.length - seriesAmount);
        if (chartPreview) chartPreview.options.setAll(customizedOptions);
      } else if (chartOptions.series?.length < seriesAmount) {
        const newSeries = merge({}, customizedOptions.series[0] ?? {});
        const len = seriesAmount - customizedOptions.series.length;
        for (let i = 0; i < len; i++) {
          let s = merge({}, newSeries);
          s._colorIndex = customizedOptions.series.length;
          customizedOptions.series.push(s);
        }
        if (chartPreview) chartPreview.options.setAll(customizedOptions);
      }
    }
  }

  return options;
}

export function parsePackedBubbleData(data, chartPreview, chartOptions, sections) {
  let addLabels = false;
  let newData = [[]];
  let labels = [[]];

  // If user has set up the label column, we need to add it to the data.
  if (sections && sections[0].extraColumns && sections[0].extraColumns.indexOf(3) > -1) {
    addLabels = true;
  }

  data.shift();

  data.sort((a, b) => a[0].localeCompare(b[0]));
  data.forEach((d) => {
    let index = newData[0].indexOf(d[0]);
    let labelIndex = labels[0].indexOf(d[0]);

    let added = false;
    let labelAdded = false;

    // Does not exist, add header
    if (index === -1) {
      index = newData[0].push(d[0], d[0]) - 2;
      if (addLabels) {
        labelIndex = labels[0].push(d[0]) - 1;
        labels.forEach((l, i) => {
          if (i) l.push(null);
        });
      }
    }

    // Go through each row in already created data and check if I can add the new value to it
    newData.some((row, rowIndex) => {
      // Skip first row (headers)
      if (rowIndex) {
        if (!row[index]) {
          row[index] = d[1];
          row[index + 1] = d[2];
          added = true;
          return true;
        }
      }
    });

    if (addLabels) {
      labels.some((row, rowIndex) => {
        if (rowIndex) {
          if (row[labelIndex] === null) {
            row[labelIndex] = d[3];
            labelAdded = true;
            return true;
          }
        }
      });
    }

    // We cannot add to the created data above so we need to create a new array
    // and add value to that instead
    if (!added) {
      const arr = new Array(newData[0].length).fill(null);
      let columnIndex = 0;
      if (index > -1) columnIndex = index;
      arr[columnIndex] = d[1];
      arr[columnIndex + 1] = d[2];
      newData.push(arr);
    }

    if (addLabels && !labelAdded) {
      const arr = new Array(labels[0].length).fill(null);
      let labelColumnIndex = 0;
      if (labelIndex > -1) labelColumnIndex = labelIndex;
      arr[labelColumnIndex] = d[3];
      labels.push(arr);
    }
  });

  if (addLabels) {
    labels[0] = labels[0].map(() => 'ev-label');
    let len = newData[0].length + labels[0].length;
    newData = newData.map((row, i) => {
      let newRow = row.concat(labels[i]);
      if (i && newRow.length < len) {
        newRow.push(...new Array(len - newRow.length).fill(null));
      }
      return newRow;
    });
  }

  return newData;
}

export function parseStreamGraphData(data, chartPreview, chartOptions) {
  if (!chartPreview) return data;
  let options = chartOptions || chartPreview.options.getAggregated();

  if (options.colors) {
    if (data && data.length && data[0].length > options.colors.length) {
      let colors = options.colors;
      const diff = data[0].length - colors.length;
      const newColors = [];

      for (let i = 0; i < diff; i++) {
        newColors.push(generateRandomColors().dark);
      }
      if (chartOptions) setAttr(chartOptions, 'colors', [...colors, ...newColors], null, true);
      else chartPreview.options.set('colors', [...colors, ...newColors], null, true);
    }
  } else if (data[0] && data[0].length) {
    const newColors = [];
    for (let i = 0; i < data[0].length; i++) {
      newColors.push(generateRandomColors().dark);
    }
    if (chartOptions) setAttr(chartOptions, 'colors', newColors, 0, true);
    else chartPreview.options.set('colors', newColors, 0, true);
  }

  return data;
}

export function parseTimelineData(data, chartPreview) {
  if (data && data[0]) {
    data[0].unshift('y');
    data.slice(1).forEach((cells) => {
      cells.unshift(0);
    });
  }

  return data;
}

export function parseSunburstData(data, chartPreview) {
  return data;
}
